// Minimal i18n. Four languages with RTL support for Arabic.
import { createContext, useContext, useEffect, useState } from "react";

const DICT = {
  en: {
    nav: { products: "Products", pricing: "Pricing", investors: "Investors", signin: "Sign in", launch: "Launch app" },
    hero: {
      eyebrow: "The AI Operating System for the Next Billion",
      title_a: "Five monopolies.",
      title_b: "One intelligence.",
      title_c: "Made for the GCC & Africa.",
      sub: "Sanaa AI replaces the world's most expensive professional software — Adobe, Bloomberg, MATLAB, AutoCAD and CATIA — with a culturally fluent, AI-native suite at a fraction of the price.",
      cta_primary: "Launch the Suite",
      cta_secondary: "Read the investor memo",
      stat_one: "5 products",
      stat_one_d: "shipping inside one suite",
      stat_two: "93%",
      stat_two_d: "gross margin on every asset",
      stat_three: "$6–8B",
      stat_three_d: "annual incumbent spend in our markets",
    },
    products: {
      eyebrow: "The Suite",
      title: "Each product breaks a different premium-software monopoly.",
      sub: "Built on the most cost-efficient Chinese AI engines and deployed across Alibaba Cloud (GCC) and Huawei Cloud (Africa).",
      open: "Open product",
    },
    why: {
      eyebrow: "Why now",
      title: "AI doesn't clone these tools. It makes them disappear.",
      p1: "For two decades, a handful of vendors owned the file formats, chat networks, and compliance workflows that gate the global economy. The lock-in wasn't features — it was the protocol.",
      p2: "Today, AI can read and write any proprietary format, translate any conversation, and deliver finished, compliant outputs directly. The membership card just lost its meaning.",
    },
    market: {
      eyebrow: "The opportunity",
      title: "40 million businesses, 400 million professionals — overpaying.",
      stats: [
        { v: "$24,000", d: "Bloomberg Terminal seat / year" },
        { v: "$800+", d: "Adobe CC seat / year (plus designer)" },
        { v: "$30,000", d: "CATIA license / year" },
        { v: "$2,000+", d: "AutoCAD / MATLAB seat / year" },
      ],
    },
    investors: {
      eyebrow: "For investors",
      title: "$1.5M seed → $15M ARR by Year 3.",
      points: [
        "Dubai HQ, GCC + Africa go-to-market",
        "Exclusive Chinese AI partnerships (DeepSeek, Qwen, ZMO.AI)",
        "85–93% gross margins, <2 month payback",
        "Multi-cloud (Alibaba Dubai, Huawei Africa)",
        "Break-even by Month 14",
      ],
      cta: "Download the investor memo",
    },
    cta: {
      title: "Stop paying the monopoly tax.",
      sub: "Sign in with a demo account and try every product in 60 seconds.",
      btn: "Try Sanaa AI",
    },
    auth: {
      welcome: "Welcome back",
      welcome_sub: "Sign in to the Sanaa AI suite.",
      create: "Create your account",
      create_sub: "Start with a free tier. No card required.",
      email: "Email",
      password: "Password",
      name: "Full name",
      role: "Your role",
      submit_login: "Sign in",
      submit_signup: "Create account",
      switch_to_signup: "New here? Create an account",
      switch_to_login: "Have an account? Sign in",
      demo: "Or try a demo account",
    },
    dashboard: {
      hello: "Hello",
      pick: "Pick a workspace",
      sub: "Five AI-native products. One identity. Switch any time.",
      launch: "Launch",
    },
  },
  ar: {
    nav: { products: "المنتجات", pricing: "الأسعار", investors: "المستثمرون", signin: "تسجيل الدخول", launch: "افتح التطبيق" },
    hero: {
      eyebrow: "نظام الذكاء الاصطناعي للمليار القادم",
      title_a: "خمسة احتكارات.",
      title_b: "ذكاء واحد.",
      title_c: "مصمم للخليج وأفريقيا.",
      sub: "تستبدل صناعة AI أغلى البرامج الاحترافية في العالم — Adobe و Bloomberg و MATLAB و AutoCAD و CATIA — بمجموعة مدركة ثقافياً وبجزء من السعر.",
      cta_primary: "ابدأ المجموعة",
      cta_secondary: "اقرأ مذكرة المستثمر",
      stat_one: "٥ منتجات",
      stat_one_d: "في مجموعة واحدة",
      stat_two: "٩٣٪",
      stat_two_d: "هامش ربح إجمالي",
      stat_three: "٦–٨ مليار $",
      stat_three_d: "إنفاق سنوي في أسواقنا",
    },
    products: {
      eyebrow: "المجموعة",
      title: "كل منتج يكسر احتكاراً مختلفاً.",
      sub: "مبني على أكفأ محركات الذكاء الاصطناعي الصينية وموزع عبر Alibaba Cloud و Huawei Cloud.",
      open: "افتح المنتج",
    },
    why: {
      eyebrow: "لماذا الآن",
      title: "الذكاء الاصطناعي يجعل هذه الأدوات تختفي.",
      p1: "لعقدين، احتكرت قلة من الشركات صيغ الملفات وشبكات المحادثة والامتثال — الإغلاق لم يكن في الميزات بل في البروتوكول.",
      p2: "اليوم، يستطيع الذكاء الاصطناعي قراءة وكتابة أي صيغة وترجمة أي محادثة وتسليم نتائج جاهزة. بطاقة العضوية فقدت معناها.",
    },
    market: {
      eyebrow: "الفرصة",
      title: "٤٠ مليون شركة و ٤٠٠ مليون محترف — يدفعون أكثر من اللازم.",
      stats: [
        { v: "٢٤٬٠٠٠$", d: "Bloomberg Terminal / سنة" },
        { v: "٨٠٠$+", d: "Adobe CC / سنة" },
        { v: "٣٠٬٠٠٠$", d: "ترخيص CATIA / سنة" },
        { v: "٢٬٠٠٠$+", d: "AutoCAD / MATLAB / سنة" },
      ],
    },
    investors: {
      eyebrow: "للمستثمرين",
      title: "١٫٥ مليون $ بذرة → ١٥ مليون $ ARR بالسنة الثالثة.",
      points: [
        "مقرها دبي، توزيع في الخليج وأفريقيا",
        "شراكات حصرية مع DeepSeek و Qwen",
        "هوامش ٨٥–٩٣٪",
        "متعدد السحاب (علي بابا، هواوي)",
        "نقطة التعادل بالشهر ١٤",
      ],
      cta: "تحميل مذكرة المستثمر",
    },
    cta: {
      title: "توقف عن دفع ضريبة الاحتكار.",
      sub: "ادخل بحساب تجريبي وجرب كل منتج خلال ٦٠ ثانية.",
      btn: "جرب Sanaa AI",
    },
    auth: {
      welcome: "مرحبا بعودتك",
      welcome_sub: "سجّل الدخول إلى مجموعة Sanaa AI.",
      create: "أنشئ حسابك",
      create_sub: "ابدأ مجاناً، بدون بطاقة.",
      email: "البريد الإلكتروني",
      password: "كلمة المرور",
      name: "الاسم الكامل",
      role: "دورك",
      submit_login: "دخول",
      submit_signup: "إنشاء حساب",
      switch_to_signup: "جديد هنا؟ أنشئ حساب",
      switch_to_login: "لديك حساب؟ سجل الدخول",
      demo: "أو جرب حساباً تجريبياً",
    },
    dashboard: {
      hello: "مرحبا",
      pick: "اختر مساحة عمل",
      sub: "خمسة منتجات. هوية واحدة. بدّل في أي وقت.",
      launch: "افتح",
    },
  },
  fr: {
    nav: { products: "Produits", pricing: "Tarifs", investors: "Investisseurs", signin: "Se connecter", launch: "Lancer l'app" },
    hero: {
      eyebrow: "L'OS d'IA pour le prochain milliard",
      title_a: "Cinq monopoles.",
      title_b: "Une seule intelligence.",
      title_c: "Conçue pour le Golfe et l'Afrique.",
      sub: "Sanaa AI remplace les logiciels les plus chers du monde — Adobe, Bloomberg, MATLAB, AutoCAD, CATIA — par une suite IA culturellement fluide, à une fraction du prix.",
      cta_primary: "Lancer la suite",
      cta_secondary: "Lire le mémo investisseur",
      stat_one: "5 produits",
      stat_one_d: "dans une seule suite",
      stat_two: "93%",
      stat_two_d: "de marge brute",
      stat_three: "6–8 Mds $",
      stat_three_d: "dépensés annuellement",
    },
    products: {
      eyebrow: "La suite",
      title: "Chaque produit casse un monopole.",
      sub: "Bâti sur les moteurs IA chinois les plus efficaces, déployé via Alibaba Cloud et Huawei Cloud.",
      open: "Ouvrir",
    },
    why: {
      eyebrow: "Pourquoi maintenant",
      title: "L'IA fait disparaître ces outils.",
      p1: "Pendant 20 ans, une poignée de vendeurs ont possédé les formats, les réseaux de chat et les workflows de conformité.",
      p2: "L'IA peut lire, écrire et traduire n'importe quel format. La carte de membre a perdu son sens.",
    },
    market: {
      eyebrow: "L'opportunité",
      title: "40 millions d'entreprises paient trop cher.",
      stats: [
        { v: "24 000$", d: "Bloomberg / an" },
        { v: "800$+", d: "Adobe CC / an" },
        { v: "30 000$", d: "CATIA / an" },
        { v: "2 000$+", d: "AutoCAD / MATLAB / an" },
      ],
    },
    investors: {
      eyebrow: "Investisseurs",
      title: "1.5M$ seed → 15M$ ARR en Y3.",
      points: [
        "Siège à Dubaï, GTM Golfe + Afrique",
        "Partenariats IA chinois exclusifs",
        "Marges 85–93%",
        "Multi-cloud (Alibaba, Huawei)",
        "Break-even mois 14",
      ],
      cta: "Télécharger le mémo",
    },
    cta: {
      title: "Arrêtez de payer la taxe du monopole.",
      sub: "Connectez-vous avec un compte démo et essayez en 60 secondes.",
      btn: "Essayer Sanaa AI",
    },
    auth: {
      welcome: "Bon retour",
      welcome_sub: "Connectez-vous à la suite Sanaa AI.",
      create: "Créer votre compte",
      create_sub: "Commencez en gratuit. Sans CB.",
      email: "Email",
      password: "Mot de passe",
      name: "Nom complet",
      role: "Votre rôle",
      submit_login: "Se connecter",
      submit_signup: "Créer le compte",
      switch_to_signup: "Nouveau ? Créer un compte",
      switch_to_login: "Déjà un compte ? Se connecter",
      demo: "Ou essayez un compte démo",
    },
    dashboard: {
      hello: "Bonjour",
      pick: "Choisissez un espace",
      sub: "Cinq produits. Une identité. Basculez à tout moment.",
      launch: "Ouvrir",
    },
  },
  sw: {
    nav: { products: "Bidhaa", pricing: "Bei", investors: "Wawekezaji", signin: "Ingia", launch: "Fungua programu" },
    hero: {
      eyebrow: "Mfumo wa AI kwa bilioni ijayo",
      title_a: "Ukiritimba mitano.",
      title_b: "Akili moja.",
      title_c: "Imeundwa kwa Ghuba na Afrika.",
      sub: "Sanaa AI inabadilisha programu ghali zaidi duniani — Adobe, Bloomberg, MATLAB, AutoCAD, CATIA — kwa bei ndogo sana na uelewa wa kitamaduni.",
      cta_primary: "Fungua Suite",
      cta_secondary: "Soma memo ya wawekezaji",
      stat_one: "Bidhaa 5",
      stat_one_d: "katika suite moja",
      stat_two: "93%",
      stat_two_d: "faida ya jumla",
      stat_three: "$6–8B",
      stat_three_d: "matumizi ya kila mwaka",
    },
    products: {
      eyebrow: "Suite",
      title: "Kila bidhaa inavunja ukiritimba.",
      sub: "Imejengwa juu ya injini bora za AI za China, kupitia Alibaba Cloud na Huawei Cloud.",
      open: "Fungua",
    },
    why: {
      eyebrow: "Kwa nini sasa",
      title: "AI inafanya zana hizi zipotee.",
      p1: "Kwa miongo miwili, kampuni chache zimemiliki miundo ya faili na mitandao ya gumzo.",
      p2: "AI inaweza kusoma na kuandika muundo wowote. Kadi ya uanachama imepoteza maana.",
    },
    market: {
      eyebrow: "Fursa",
      title: "Biashara milioni 40 zinalipa zaidi.",
      stats: [
        { v: "$24,000", d: "Bloomberg / mwaka" },
        { v: "$800+", d: "Adobe CC / mwaka" },
        { v: "$30,000", d: "Leseni ya CATIA / mwaka" },
        { v: "$2,000+", d: "AutoCAD / MATLAB / mwaka" },
      ],
    },
    investors: {
      eyebrow: "Wawekezaji",
      title: "$1.5M seed → $15M ARR mwaka wa 3.",
      points: [
        "Makao makuu Dubai",
        "Ushirikiano wa AI wa China",
        "Faida 85–93%",
        "Cloud nyingi",
        "Break-even mwezi 14",
      ],
      cta: "Pakua memo",
    },
    cta: {
      title: "Acha kulipa kodi ya ukiritimba.",
      sub: "Ingia na akaunti ya demo, jaribu kila kitu kwa sekunde 60.",
      btn: "Jaribu Sanaa AI",
    },
    auth: {
      welcome: "Karibu tena",
      welcome_sub: "Ingia kwenye suite ya Sanaa AI.",
      create: "Fungua akaunti",
      create_sub: "Anza bure. Bila kadi.",
      email: "Barua pepe",
      password: "Nenosiri",
      name: "Jina kamili",
      role: "Jukumu lako",
      submit_login: "Ingia",
      submit_signup: "Fungua akaunti",
      switch_to_signup: "Mpya hapa? Fungua akaunti",
      switch_to_login: "Una akaunti? Ingia",
      demo: "Au jaribu akaunti ya demo",
    },
    dashboard: {
      hello: "Habari",
      pick: "Chagua mahali pa kazi",
      sub: "Bidhaa tano. Utambulisho mmoja. Badilisha wakati wowote.",
      launch: "Fungua",
    },
  },
};

const I18nContext = createContext({ lang: "en", t: (k) => k, setLang: () => {} });

export function I18nProvider({ children }) {
  const [lang, setLangState] = useState(() => localStorage.getItem("sanaa_lang") || "en");

  const setLang = (l) => {
    localStorage.setItem("sanaa_lang", l);
    setLangState(l);
  };

  useEffect(() => {
    const dir = lang === "ar" ? "rtl" : "ltr";
    document.documentElement.dir = dir;
    document.documentElement.lang = lang;
  }, [lang]);

  const t = (path) => {
    const parts = path.split(".");
    let node = DICT[lang] || DICT.en;
    for (const p of parts) {
      node = node?.[p];
      if (node === undefined) {
        node = path;
        // fallback to English
        let f = DICT.en;
        for (const pp of parts) f = f?.[pp];
        return f ?? path;
      }
    }
    return node;
  };

  return (
    <I18nContext.Provider value={{ lang, setLang, t }}>
      {children}
    </I18nContext.Provider>
  );
}

export const useI18n = () => useContext(I18nContext);

export const LANGS = [
  { code: "en", label: "English", short: "EN" },
  { code: "ar", label: "العربية", short: "ع" },
  { code: "fr", label: "Français", short: "FR" },
  { code: "sw", label: "Kiswahili", short: "SW" },
];
